export interface ScholarshipApplicationDetailsProps {
  id: number;
  name: string;
  cnic_number: string;
  gender: string;
  age: number;
  residential_address: string;
  cell_phone: string;
  telephone: string;
  email: string;
  photo: string;
  educational_history: EducationalHistory[];
  scholarship_request_details: ScholarshipRequestDetails;
  other_scholarships: OtherScholarship[];
  guardian_details: GuardianDetails;
  studying_family_members: StudyingFamilyMember[];
  earning_family_members: EarningFamilyMember[];
  accommodation_details: AccommodationDetails;
  family_liabilities: FamilyLiability[];
  family_expenses: FamilyExpense[];
  personal_statements: PersonalStatement[];
  documents_attached: DocumentAttached[];
  extra_documents_attached?: DocumentAttached[];
  comments: Comment[];
  status: string;
  rejection_reason: string;
  amount_paid_till_now: number;
  amount_granted: number;
  interviews: Interview[];
  mentors: MentorAssigned[];
  donors_tagged: DonorTagged[];
  is_resubmission_allowed: boolean;
  auto_evaluation_score: number;
  is_special_case: boolean;
  social_participation_score: number;
  parent_application?: ScholarshipApplicationDetailsProps;
}

export interface ScholarshipApplicationsListingProps {
  id: number;
  name: string;
  parent_application: number | null;
  cnic_number: string;
  gender: string;
  age: number;
  residential_address: string;
  cell_phone: string;
  telephone: string;
  email: string;
  photo: string;
  status: string;
  is_resubmission_allowed: boolean;
  rejection_reason: string;
  interviews: Interview[];
  donors_tagged: DonorTagged[];
  auto_evaluation_score: number;
  amount_paid_till_now: number;
  amount_granted: number;
  contract_signed?: boolean;
  generated_contract?: string | null;
}
export interface CommentBy {
  id: number;
  name: string;
  email: string;
  phone_number: string | null;
  role: string;
  profile_picture_url: string;
}

export interface Comment {
  id: number;
  comment_by: CommentBy;
  comment: string;
}

export interface EducationalHistory {
  institute: string;
  score: number;
  is_secondary_school: boolean;
  is_higher_school: boolean;
  is_university: boolean;
  score_is_percentage: boolean;
  current_semester?: number;
  total_semesters?: number;
}

export interface ScholarshipRequestDetails {
  degree_discipline: string;
  degree_name: string;
  degree_institute: string;
  degree_institute_custom?: string;
  degree_semester: number;
  degree_recognized_by: string;
  tuition_fee_per_semester: number;
  admission_fee: number;
  tuition_fee_per_semester_in_words: string;
  self_contribution_percentage: number;
}

export interface OtherScholarship {
  institute_name: string;
  total_amount: number;
  duration_in_years: number;
  class_or_degree: string;
}
export interface StudyingFamilyMember {
  name: string;
  relation: string;
  institute: string;
  degree: string;
  fee: number;
}

export interface AccommodationDetails {
  accommodation_type: string;
  accommodation_ownership: string;
  accommodation_ownership_joint_or_independent: string;
  monthly_rent: number;
}
export interface FamilyLiability {
  amount: number;
  nature: string;
  description: string;
}

export interface FamilyExpense {
  amount: number;
  nature: string;
  description: string;
}

export interface PersonalStatement {
  question: string;
  response: string;
}

export interface DocumentAttached {
  title: string;
  attachment_url: string[];
}

interface Interview {
  interviewer: Interviewer;
  scheduled_by: ScheduledBy;
  interview_date: string;
  feedback: InterviewFeedback[];
}

interface Interviewer {
  id: number;
  name: string;
  email: string;
  phone_number: string | null;
  role: "Interviewer";
  profile_picture_url: string;
}

interface ScheduledBy {
  id: number;
  name: string;
  email: string;
  phone_number: string | null;
  role: "Admin";
  profile_picture_url: string;
}

interface InterviewFeedback {
  scholarship_application_interview: number;
  commitment_to_studies_rating: number;
  comments_related_to_academic_performance: string;
  leadership_potential_rating: number;
  community_involvement_rating: number;
  initiative_and_creativity_rating: number;
  comments_related_to_leadership: string;
  alignment_with_scholarship_objectives_rating: number;
  vision_for_future_contribution_to_society_rating: number;
  comments_related_to_values: string;
  final_remarks: string;
}

export interface DonorTagged {
  donor_tagged: Donor;
  tagged_amount: number;
  tagged_by: Admin;
}

interface Donor {
  id: number;
  name: string;
  email: string;
  phone_number: string | null;
  role: "Donor";
  profile_picture_url: string;
}

interface Admin {
  id: number;
  name: string;
  email: string;
  phone_number: string | null;
  role: "Admin";
  profile_picture_url: string;
}

export enum APPLICATION_STATUS {
  "SUBMITTED" = "Submitted",
  "APPROVED" = "Approved",
  "REJECTED" = "Rejected",
  "IN_PROCESS" = "In Process",
  "PAID" = "Paid",
}

export interface GuardianDetails {
  name: string;
  cnic_number: string;
  cell_phone: string;
  occupation: string;
  designation: string;
  employer: string;
  monthly_income: number;
  pension: number;
  family_members_count: number;
}

export interface EarningFamilyMember {
  name: string;
  relation: string;
  organization: string;
  occupation: string;
  income: number;
}

export interface ScholarshipApplicationForm {
  id?: number;
  name: string;
  cnic_number: string;
  gender: string;
  age: number;
  residential_address: string;
  cell_phone: string;
  telephone: string;
  email: string;
  photo: string;
  educational_history: EducationalHistory[];
  scholarship_request_details: ScholarshipRequestDetails;
  other_scholarships: OtherScholarship[];
  guardian_details: GuardianDetails;
  studying_family_members: StudyingFamilyMember[];
  earning_family_members: EarningFamilyMember[];
  accommodation_details: AccommodationDetails;
  family_liabilities: FamilyLiability[];
  family_expenses: FamilyExpense[];
  personal_statements: PersonalStatement[];
  documents_attached: DocumentAttached[];
}

export interface MentorFeedback {
  scholarship_application_id: number;
  future_goals_rating: number;
  comments_related_to_future_goals: string;
  career_awareness_rating: number;
  social_contribution_rating: number;
  comments_related_to_social_contribution: string;
  resilience_rating: number;
  comments_related_to_resilience: string;
  skills_development_rating: number;
  comments_related_to_skills_development: string;
  future_education_readiness_rating: number;
  comments_related_to_future_education_readiness: string;
  final_remarks: string;
}

export interface MentorFeedbackResponse {
  id: number;
  overall_rating: number;
  created_at: string;
  updated_at: string;
  future_goals_rating: number;
  comments_related_to_future_goals: string;
  career_awareness_rating: number;
  social_contribution_rating: number;
  comments_related_to_social_contribution: string;
  resilience_rating: number;
  comments_related_to_resilience: string;
  skills_development_rating: number;
  comments_related_to_skills_development: string;
  future_education_readiness_rating: number;
  comments_related_to_future_education_readiness: string;
  final_remarks: string;
  scholarship_application_mentor: number;
}

export interface MentorAssigned {
  id: number;
  mentor: Mentor;
  assigned_by: Admin;
  feedback: MentorFeedbackResponse[];
}

export interface Mentor {
  id: number;
  name: string;
  email: string;
  phone_number: string | null;
  role: "Mentor";
  profile_picture_url: string;
}

export interface MentorAssignment {
  scholarship_application_id: number;
  mentor_id: number;
}

export type PaginationProps = {
  page_number: number;
  page_size: number;
  total_no_of_records: number;
  total_no_of_pages: number;
};
